import { Component, ViewChild } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LoadingController, ToastController } from "@ionic/angular";
import { debounceTime, distinctUntilChanged, pairwise, startWith } from "rxjs/operators";
import { BaseClass } from "src/app/shared/classes/base";
import { TranslationService } from "src/app/shared/services/translation.service";
import { environment } from "../../environments/environment";
import { Goal, GoalStatusEnum } from "../goals/_interfaces/goal.interface";
import { AutoSaveComponent } from "../shared/components/autosave/autosave.component";
import { AttributeItem } from "../shared/interfaces/attribute-item";
import { ProcessConfig, QuestionConfig, QuestionGroup, QuestionTypeEnum } from "../shared/interfaces/process-config.interface";
import { ProcessStatusEnum } from "../shared/interfaces/process.interface";
import { QuestionGroupAttributesPipe } from "../shared/pipes/question-group-attributes.pipe";
import { UtilService } from "../shared/services/util.service";
import { SelfEvaluationRecord } from "./_interfaces/self-evaluation-record.interface";
import { SelfEvaluationService } from "./self-evaluation.service";
import { QuestionAttributePipe } from "src/app/shared/pipes/question-attribute.pipe";
import { ConfigService } from "../shared/services/multi-clients/config.service";

@Component({
    selector: "app-self-evaluation",
    templateUrl: "./self-evaluation.page.html",
    styleUrls: ["./self-evaluation.page.scss"],
})
export class SelfEvaluationPage extends BaseClass {
    @ViewChild("autosave", { static: true }) autosave: AutoSaveComponent;

    private hasGoalsInputOnSelfEval: boolean;

    public user: { attributes: AttributeItem[] };
    public processConfig: ProcessConfig;
    public allQuestions: Array<QuestionConfig> = [];
    public formGroup: UntypedFormGroup;
    public status: ProcessStatusEnum;
    public goalsStepStatus: GoalStatusEnum;
    public allowAutoSave: boolean = true;
    public autoSaving: boolean = false;
    public timerStart: boolean = false;
    public hasPendingChanges: boolean = false;
    public hasTwoPlusTwoCoaching: boolean = false;
    public goals: Array<Goal>;
    public timerId;
    public questionsType;
    public hideTwoPlusTwoButton: boolean;
    public hideQuestionNumber: boolean;;

    private _cycleId: number;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: SelfEvaluationService,
        private _loadingController: LoadingController,
        private _utilService: UtilService,
        private _questionGroupsAttributePipe: QuestionGroupAttributesPipe,
        private _questionAttributePipe: QuestionAttributePipe,
        private _configService: ConfigService

    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(this._activatedRoute.snapshot.paramMap.get("cycleId"), 10);
        this.hasGoalsInputOnSelfEval = this._configService.settings.features.hasGoalsInputOnSelfEval;
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
        this.hideTwoPlusTwoButton = this._configService.settings.features.hideTwoPlusTwoButton;
        this.hideQuestionNumber = this._configService.settings.features.hideQuestionNumber;
    }

    ionViewWillEnter() {
        this._loadEvaluation();
    }

    public goBack(): void {
        this._router.navigate(["dashboard"]);
    }

    public goToNotepad(): void {
        this._setReturnPath("Notepad_ReturnPath");
        this._router.navigate(["notepad"]);
    }

    public goToCoaching(): void {
        this._setReturnPath("GoalsReview_ReturnPath");
        this._router.navigate(["goals-review/" + this._cycleId]);
    }

    public save(formGroup: UntypedFormGroup, notificate: boolean = false): void {
        this.autoSaving = true;

        const goalsIndexes = this._utilService.getGoalsFieldsIndexes(this.processConfig.questionsGroups);
        const evaluation = this._utilService.adjustRatingsForGoals(this.processConfig.questionsGroups, formGroup.getRawValue()) as SelfEvaluationRecord;

        this.timerStart = false;
        clearTimeout(this.timerId);

        this._evaluationService.saveSelfEvaluation(evaluation).subscribe(
            () => {
                if (notificate) {
                    this.hasPendingChanges = false;

                    let goalsWeight = 0;
                    goalsIndexes.forEach((element) => {
                        let goal = this.goals.find((el) => {
                            return el.id === element.id;
                        });
                        goalsWeight += goal.weight;
                    });

                    if (this.hasGoalsInputOnSelfEval && goalsIndexes.length === 0) {
                        this.notify(this._translationService.getTranslation("selfEvaluation.savedWithoutEvalFromGoals") + ".", "success", 5000);
                        this.autoSaving = false;
                        return;
                    }
                    if (this.hasGoalsInputOnSelfEval && goalsIndexes.length !== 0 && goalsWeight !== 100) {
                        this.notify(this._translationService.getTranslation("selfEvaluation.selfSavedButGoalsWithWrongSum") + ".", "success", 5000);
                        this.autoSaving = false;
                        return;
                    }
                    if (
                        this.hasGoalsInputOnSelfEval &&
                        goalsIndexes.length !== 0 &&
                        this.goalsStepStatus in [GoalStatusEnum.Draft, GoalStatusEnum.WaitingForApproval, GoalStatusEnum.Rejected]
                    ) {
                        this.notify(this._translationService.getTranslation("selfEvaluation.sendGoalsToSendSelfEval") + ".", "success", 5000);
                        this.autoSaving = false;
                        return;
                    }
                    this.notifySuccess();
                }

                this.autoSaving = false;
            },
            (error) => {
                this.notifyError(error);
                this.autoSaving = false;
            },
        );
    }

    public send(formGroup: UntypedFormGroup): void {
        const goalsIndexes = this._utilService.getGoalsFieldsIndexes(this.processConfig.questionsGroups);

        let goalsWeight = 0;
        goalsIndexes.forEach((element) => {
            let goal = this.goals.find((el) => {
                return el.id === element.id;
            });
            goalsWeight += goal.weight;
        });

        if (this.hasGoalsInputOnSelfEval && goalsIndexes.length !== 0 && goalsWeight !== 100) {
            this.notify(this._translationService.getTranslation("selfEvaluation.weightsWrongSum") + ". ", "danger", 5000);
            return;
        }

        if (
            this.hasGoalsInputOnSelfEval &&
            goalsIndexes.length === 0 &&
            this.goalsStepStatus in [GoalStatusEnum.Draft, GoalStatusEnum.WaitingForApproval, GoalStatusEnum.Rejected]
        ) {
            this.notify(this._translationService.getTranslation("selfEvaluation.goalsInputsFromSelfEvalNotSent") + ".", "danger", 5000);
            return;
        }

        if (this.hasGoalsInputOnSelfEval && goalsIndexes.length > 0 && this.goalsStepStatus in [GoalStatusEnum.Draft, GoalStatusEnum.WaitingForApproval, GoalStatusEnum.Rejected]) {
            this.notify(this._translationService.getTranslation("selfEvaluation.goalsNotSent") + ".", "danger", 5000);
            return;
        }
        formGroup.updateValueAndValidity();

        if (!formGroup.valid) {
            formGroup["missingFields"] = true;

            this.notify(this._translationService.getTranslation("generic.requiredValueNotProvided"), "danger");

            return;
        }

        const evaluation = this._utilService.adjustRatingsForGoals(this.processConfig.questionsGroups, formGroup.getRawValue()) as SelfEvaluationRecord;

        this._evaluationService.sendSelfEvaluation(evaluation).subscribe(
            () => {
                this.notifySuccess(this._translationService.getTranslation("selfEvaluation.dataSavedAndSent") + ".");
                this.goBack();
            },
            (error) => this.notifyError(error),
        );
    }

    private _createFormGroup(groups: Array<QuestionGroup>, editingEvaluation: SelfEvaluationRecord): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            cycleId: new UntypedFormControl(this._cycleId),
        });

        const goalsIndexes = this._utilService.getGoalsFieldsIndexes(groups);
        let currentIndex = 1;

        if (groups) {
            const displayedGroups = this._questionGroupsAttributePipe.transform(groups, this.user.attributes);

            groups.forEach((group) => {
                group.questions.forEach((question) => {
                    const controlName = "field" + currentIndex;
                    const validators =
                        displayedGroups.some((g) => g.id === group.id) &&
                            ((question.required && question.typeConfig.type !== this.QuestionType.Calculate) || question.goal) &&
                            question.hideQuestion !== true
                            ? [Validators.required]
                            : [];

                    formGroup.addControl(
                        controlName,
                        new UntypedFormControl(
                            {
                                value: this._utilService.getEvaluationValue(question, editingEvaluation, goalsIndexes, currentIndex),
                                disabled: this.status !== ProcessStatusEnum.WaitingInput,
                            },
                            validators,
                        ),
                    );

                    question.currentIndex = currentIndex;
                    currentIndex++;
                });
            });
        }

        return formGroup;
    }

    private async _loadEvaluation(): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._evaluationService.getSelfEvaluation(this._cycleId).subscribe(
            async (response) => {
                this.user = response.data.user;
                this.processConfig = response.data.config;
                this.status = response.data.status;
                this.goals = response.data.goals;
                this.goalsStepStatus = response.data.goalsStepStatus;
                this.hasTwoPlusTwoCoaching = response.data.hasTwoPlusTwoCoaching;
                if (this.processConfig.questions) {
                    this.allQuestions = [...this.allQuestions, ...this.processConfig.questions];
                } else {
                    let questions: QuestionConfig[] = [];
                    this.processConfig.questionsGroups.forEach((group) => {
                        questions = [...questions, ...group.questions];
                        questions = this._questionAttributePipe.transform(questions, this.user.attributes);
                    });
                    this.allQuestions = [...this.allQuestions, ...questions];
                }

                this.processConfig.questionsGroups = this.processConfig.questionsGroups.sort((a, b) => a.order - b.order);

                this.processConfig.questionsGroups = this._utilService.addGoalsAsQuestions(response.data.goals, this.processConfig.questionsGroups);

                const formGroup = this._createFormGroup(this.processConfig.questionsGroups, response.data.evaluation);

                if (this.processConfig.questionsGroups) {
                    this.processConfig.questionsGroups.forEach((group) => {
                        group.questions = this._utilService.groupQuestions(group.questions);
                        group.onlyCalculateQuestions = group.questions.every((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
                        group.hasCalculateQuestions = this.hasCalculateType(group);
                    });
                }

                this.formGroup = formGroup;

                if (this.autosave) {
                    this.autosave.autoSaveAllowed$.subscribe((value: boolean) => {
                        this.allowAutoSave = value;
                    });
                }

                this.formGroup.valueChanges.pipe(startWith(this.formGroup.value), distinctUntilChanged(), pairwise()).subscribe(async ([previous, current]) => {
                    if (JSON.stringify(previous) === JSON.stringify(current)) return;
                    this.allowAutoSave = false;
                    const valuesToSet = await this._utilService.calculateRelatedQuestions(current, this.allQuestions, previous);
                    valuesToSet.forEach((result) => {
                        this.formGroup.get(result.field).setValue(result.value, { emitEvent: true, onlySelf: true });
                    });
                    this.allowAutoSave = true;
                });

                this.formGroup.valueChanges.pipe(distinctUntilChanged(), debounceTime(15000)).subscribe((evaluation) => {
                    if (this.allowAutoSave && !this.timerStart) {
                        this.timerStart = true;
                        this._autoSave();
                    }
                });

                loading.dismiss();
            },
            (error) => {
                loading.dismiss();
                this.notifyError(error);
            },
        );
    }

    private _autoSave() {
        this.timerStart = false;
        this.hasPendingChanges = this.autoSaving && true;
        if (!this.autoSaving) {
            this.save(this.formGroup, false);
        }
    }

    private _setReturnPath(storageKey: string): void {
        localStorage.setItem(storageKey, "self-evaluation/" + this._cycleId);
    }

    private hasCalculateType(group: QuestionGroup) {
        return group.questions.some((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
    }
}
